import React from 'react';
import PropTypes from 'prop-types';

import { useParams, useSearchParams } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';

import { styled } from '@mui/material/styles';

import { t } from 'i18next';
import { Box } from '@mui/material';

import {
  Label,
  Button,
  FormPasswordField,
  Link,
  KeyIcon,
  Typography,
} from '@datanomik/commons-ui';

import { ArrowRightSLine, UserShared2Line } from '@datanomik/commons-ui/icons';

import useResetPassword from './hooks';

import Grid from '../../common/Grid';
import PasswordStrength from '../../common/PasswordStrength';
import Sticky from '../../common/Sticky';

import ContentBox from '../LogIn/ContentBox';

import styles from './styles';

const ResetPasswordRaw = ({ className }) => {
  const { token } = useParams();
  const [data] = useSearchParams();
  const {
    formMethods,
    passwordPolicies,
    handleOnSubmit,
  } = useResetPassword(token);

  const {
    MIN_LOWERCASE_CHARACTERS,
    MIN_UPPERCASE_CHARACTERS,
    MIN_NUMERIC_CHARACTERS,
    MIN_SPECIAL_CHARACTERS,
    MIN_LENGTH,
  } = passwordPolicies;

  const passwordExpiredValue = data?.get('passwordExpired') === 'true';

  return (
    <Box className={`${className}`}>
      <Box className="dn-container">
        <Grid container>
          <Grid item xs={12} md="auto">
            <Sticky position="top" rootSelector="body">
              <ContentBox />
            </Sticky>
          </Grid>
          <Grid item xs={12} md className="dn-flex-center">
            <Box maxWidth="350px" m="auto 0">
              <Grid container>
                <Grid item xs={12} textAlign="center">
                  <KeyIcon size="xlarge" color="neutral">
                    <UserShared2Line />
                  </KeyIcon>
                </Grid>
                <Grid item xs={12} textAlign="center" mb="14px">
                  {
                    passwordExpiredValue ? (
                      <Typography variant="p3" color="text.500">
                        {t('The password has expired and needs to be updated.')}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="h5">
                          {t('Create a new password')}
                        </Typography>
                        <Typography variant="p3" color="text.500" mt="4px">
                          {t('Set up a secure password to protect your account.')}
                        </Typography>
                      </>
                    )
                  }
                </Grid>
                <Grid item xs={12}>
                  <Box maxWidth="350px" m="0 auto">
                    <FormProvider {...formMethods}>
                      <form onSubmit={formMethods.handleSubmit(handleOnSubmit)} noValidate>
                        <Grid container className="dn-grid-medium">
                          <Grid item xs={12}>
                            <FormPasswordField
                              name="newPassword"
                              control={formMethods.control}
                              label={<Label text={t('New Password')} />}
                              labelText={t('New Password')}
                              required
                              validationRules={{
                                validate: {
                                  lowerChar: (v) => new RegExp(`^(?=.*[a-z]{${MIN_LOWERCASE_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} lowercase alphabetical characters.', { count: MIN_LOWERCASE_CHARACTERS }),
                                  upperChar: (v) => new RegExp(`^(?=.*[A-Z]{${MIN_UPPERCASE_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} uppercase alphabetical characters.', { count: MIN_UPPERCASE_CHARACTERS }),
                                  numberChar: (v) => new RegExp(`^(?=.*[0-9]{${MIN_NUMERIC_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} numeric characters.', { count: MIN_NUMERIC_CHARACTERS }),
                                  strangeChar: (v) => new RegExp(`^(?=.*[^a-zA-Z0-9]{${MIN_SPECIAL_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} special characters.', { count: MIN_SPECIAL_CHARACTERS }),
                                  lengthChar: (v) => new RegExp(`^(?=.{${MIN_LENGTH},})`).test(v) || t('The password must be {{count}} characters or longer.', { count: MIN_LENGTH }),
                                },
                              }}
                              localeText={{
                                isRequired: t('IS REQUIRED'),
                              }}
                              slotProps={{
                                htmlInput: {
                                  autoComplete: 'off',
                                },
                              }}
                            />
                            <Box p="4px">
                              <PasswordStrength password={formMethods.watch('newPassword')} />
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <FormPasswordField
                              name="repeatNewPassword"
                              control={formMethods.control}
                              label={<Label text={t('Repeat New Password')} />}
                              labelText={t('Repeat New Password')}
                              required
                              validationRules={{
                                validate: {
                                  passEquals: (v) => v === formMethods.watch('newPassword') || t('Please make sure your passwords match.'),
                                },
                              }}
                              localeText={{
                                isRequired: t('IS REQUIRED'),
                              }}
                              slotProps={{
                                htmlInput: {
                                  autoComplete: 'off',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button fullWidth type="submit" variant="contained">
                              {t('Change Password')}
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    </FormProvider>
                  </Box>
                </Grid>
                <Grid item xs={12} textAlign="right">
                  <Box maxWidth="350px" m="0 auto">
                    <Link href="/#/login">
                      {t('Login')}
                      <ArrowRightSLine />
                    </Link>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

ResetPasswordRaw.propTypes = {
  className: PropTypes.string,
};

export const ResetPassword = styled(ResetPasswordRaw)(styles);

ResetPassword.propTypes = ResetPasswordRaw.propTypes;
